/**
 * Pure helpers for the sysadmin chat-driven function authoring route
 * (functions-expansion 2026-05-12). Extracted into a sibling file so
 * unit tests can exercise the validation + mapping logic without
 * mounting React.
 *
 * See catalog.functions.new.tsx for the consumer.
 */

// Canonical category list for sysadmin wrapper functions. Pinned here
// so unit tests can verify drift against the route file.
export const SYSADMIN_FUNCTION_CATEGORIES = [
  'classification',
  'pii',
  'prompt-injection',
  'safety',
  'memory',
  'retrieval',
  'citation',
  'faithfulness',
  'guardrail',
  'audit',
  'format',
  'custom',
] as const;

export type SysadminFunctionCategory = (typeof SYSADMIN_FUNCTION_CATEGORIES)[number];

export const SYSADMIN_FUNCTION_APPLIES_TO = ['inlet', 'outlet', 'both'] as const;
export type SysadminFunctionAppliesTo = (typeof SYSADMIN_FUNCTION_APPLIES_TO)[number];

/**
 * Function-key slug regex. Must match what the backend POST endpoint
 * (`/admin/platform/argus-wrapper-functions`) expects.
 */
export const FUNCTION_KEY_RE = /^[a-z0-9_.-]+$/;

export interface SysadminFunctionDraft {
  /** Slug per FUNCTION_KEY_RE. */
  functionKey: string;
  /** Chat-helper provides this via set_metadata. */
  displayName: string;
  /** Chat-helper provides this via set_metadata. */
  description: string;
  /** Chat-helper provides this via set_code_body. */
  source: string;
  /** UI is python|typescript; backend wants py|ts. */
  language: 'typescript' | 'python';
  category: SysadminFunctionCategory;
  appliesTo: SysadminFunctionAppliesTo;
  latencyBudgetMs: number;
  /** Semver-ish; backend accepts any non-empty string. */
  version: string;
}

export type SysadminFunctionValidationError =
  | { field: 'functionKey'; code: 'REQUIRED' | 'INVALID_FORMAT' }
  | { field: 'displayName'; code: 'REQUIRED' }
  | { field: 'description'; code: 'REQUIRED' }
  | { field: 'source'; code: 'REQUIRED' }
  | { field: 'latencyBudgetMs'; code: 'OUT_OF_RANGE' }
  | { field: 'version'; code: 'REQUIRED' };

/**
 * Validate a chat-driven sysadmin function draft. Returns an empty
 * array when valid. Mirrors the toast-driven validation in the route
 * but returns structured errors so unit tests can pin individual
 * field failures.
 */
export function validateSysadminFunctionDraft(
  draft: SysadminFunctionDraft,
): SysadminFunctionValidationError[] {
  const errs: SysadminFunctionValidationError[] = [];

  const fk = draft.functionKey.trim();
  if (!fk) {
    errs.push({ field: 'functionKey', code: 'REQUIRED' });
  } else if (!FUNCTION_KEY_RE.test(fk)) {
    errs.push({ field: 'functionKey', code: 'INVALID_FORMAT' });
  }

  if (!draft.displayName.trim()) {
    errs.push({ field: 'displayName', code: 'REQUIRED' });
  }
  if (!draft.description.trim()) {
    errs.push({ field: 'description', code: 'REQUIRED' });
  }
  if (!draft.source.trim()) {
    errs.push({ field: 'source', code: 'REQUIRED' });
  }
  if (
    !Number.isFinite(draft.latencyBudgetMs) ||
    draft.latencyBudgetMs < 1 ||
    draft.latencyBudgetMs > 10_000
  ) {
    errs.push({ field: 'latencyBudgetMs', code: 'OUT_OF_RANGE' });
  }
  if (!draft.version.trim()) {
    errs.push({ field: 'version', code: 'REQUIRED' });
  }
  return errs;
}

/**
 * Map ToolDeveloperPage's 'typescript' | 'python' language into the
 * wrapper-function backend's 'ts' | 'py' enum. The backend rejects
 * anything else; the chat helper occasionally emits sql/http but
 * ToolDeveloperPage pre-narrows the language state so we never see
 * those values here. Defensive default to 'ts' just in case.
 */
export function languageToWrapperRuntime(
  language: 'typescript' | 'python',
): 'ts' | 'py' {
  return language === 'python' ? 'py' : 'ts';
}

/**
 * Build the wrapper-function create payload from a valid draft. Pure
 * function — does not call the API. Mirrors what
 * `useCreateWrapperFunction.mutateAsync` expects.
 */
export function buildSysadminCreatePayload(draft: SysadminFunctionDraft): {
  functionKey: string;
  displayName: string;
  description: string;
  runtime: 'ts' | 'py';
  source: string;
  category: string;
  appliesTo: SysadminFunctionAppliesTo;
  latencyBudgetMs: number;
  version: string;
} {
  return {
    functionKey: draft.functionKey.trim(),
    displayName: draft.displayName.trim(),
    description: draft.description.trim(),
    runtime: languageToWrapperRuntime(draft.language),
    source: draft.source,
    category: draft.category,
    appliesTo: draft.appliesTo,
    latencyBudgetMs: draft.latencyBudgetMs,
    version: draft.version.trim(),
  };
}
